import {runInAction} from 'mobx'
import {uiBreak} from './uiBreak'
import {isPromise} from './promises'
import {DefaultMap} from './maps'

export function arrayReplaceAll<T>(array: T[], newItems: T[]): void {
    if (!newItems) {
        newItems = []
    }
    array.splice(0, array.length, ...newItems)
}

export function arrayClear<T>(array: T[]): void {
    array.splice(0, array.length)
}

/**
 * Splits `array` into batches of maximum size `batchSize`. Last batch may have less items.
 * Each invocation of `map` function gets its own array.
 * @param array Input array to split into batches
 * @param batchSize Maximum size of batch
 * @param map Function called for every batch. Expects to return a value which will be added to result of `mapBatches`.
 * @returns List of return values of `map` function.
 */
export function mapBatches<T, U>(array: T[], batchSize: number, map: (batch: T[]) => U): U[] {
    const result: U[] = []
    let batch: T[] = []

    const mapBatch = () => {
        if (batch.length < 1) {
            return
        }

        result.push(map(batch))
        batch = []
    }

    array.forEach((item, idx) => {
        batch.push(item)

        if ((idx + 1) % batchSize === 0) {
            mapBatch()
        }
    })

    mapBatch()

    return result
}

export function arrayPushAll<T>(array: T[], items: Iterable<T>): void {
    array.push(...items)
}

function* arrayMapRecursiveImpl<El, T>(array: El[], map: (x: El) => [El[], T]): Generator<T> {
    for (const el of array) {
        const [next, item] = map(el)
        yield item
        yield* arrayMapRecursiveImpl(next, map)
    }
}

export function arrayMapRecursive<El, T>(array: El[], map: (x: El) => [El[], T]): T[] {
    return Array.from(arrayMapRecursiveImpl(array, map))
}

export function arrayDeleteFirst<El>(array: El[], predicate: (value: El, index: number, obj: El[]) => boolean): void {
    const idx = array.findIndex(predicate)
    if (idx >= 0) {
        array.splice(idx, 1)
    }
}

export function arrayDeleteWhere<El>(array: El[], predicate: (value: El) => boolean): void {
    for (let i = 0; i < array.length; i++) {
        if (predicate(array[i])) {
            array.splice(i, 1)
            i--
        }
    }
}

function arrayCompareObjectsOneWay<El>(array1: El[], array2: El[], equals: (item1: El, item2: El) => boolean): boolean {
    return !array1.some(item1 => !array2.find(item2 => equals(item1, item2)))
}

export function arrayCompareObjects<El>(
    array1: El[],
    array2: El[],
    equals: (item1: El, item2: El) => boolean
): boolean {
    return (
        array1.length === array2.length &&
        arrayCompareObjectsOneWay(array1, array2, equals) &&
        arrayCompareObjectsOneWay(array2, array1, (item2, item1) => equals(item1, item2))
    )
}

export function* arrayChunks<T>(arr: T[], n: number): Generator<T[], void> {
    for (let i = 0; i < arr.length; i += n) {
        yield arr.slice(i, i + n)
    }
}

type HasChildren<T, TChildrenPropertyName extends keyof T> = {
    [key in TChildrenPropertyName]: T[]
}

/**
 * Returns recursively flattened list of items. Items are returned in preorder order (item itself before it children).
 * This method expects item children are stored in property "children".
 * @param items List of items to flatten.
 */
export function arrayRecursiveFlatten<T extends HasChildren<T, 'children'>>(items: T[]): Iterable<T>
/**
 * Returns recursively flattened list of items. Items are returned in preorder order (item itself before it children).
 * @param items List of items to flatten.
 * @param childrenPropertyName Property name where item's children are stored.
 */
export function arrayRecursiveFlatten<T extends HasChildren<T, TPropertyName>, TPropertyName extends keyof T>(
    items: T[],
    childrenPropertyName: TPropertyName
): Iterable<T>
export function* arrayRecursiveFlatten<T extends HasChildren<T, TPropertyName>, TPropertyName extends keyof T>(
    items: T[],
    childrenPropertyName?: TPropertyName
): Iterable<T> {
    if (typeof childrenPropertyName === 'undefined') {
        childrenPropertyName = 'children' as TPropertyName
    }

    for (const item of items) {
        yield item
        yield* arrayRecursiveFlatten(item[childrenPropertyName], childrenPropertyName)
    }
}

export function arrayHasDuplicates<T>(array: T[]): boolean {
    return new Set(array).size !== array.length
}

/**
 * Processes `items` in batches of size `batchSize`.
 * The `batchAction` will be called and awaited for each batch followed by awaiting `uiBreak`.
 * @param items Array of items to process.
 * @param batchAction Action to be called for every batch.
 * @param batchSize Size of batch.
 */
export async function processInAsyncBatches<T>(
    items: readonly T[],
    batchAction: (batch: T[]) => Promise<void> | void,
    batchSize = 100
): Promise<void> {
    const batches = Array(Math.ceil(items.length / batchSize))
        .fill(0)
        .map((_, i) => items.slice(i * batchSize, Math.min(items.length, (i + 1) * batchSize)))

    for (const batch of batches) {
        await batchAction(batch)
        await uiBreak()
    }
}

/**
 * Processes `items` in batches of size `batchSize`.
 * The `itemAction` will be called and awaited for each item. The `uiBreak` will be called after every batch.
 * Processing of one batch will be wrapped in `runInAction` if provided `itemAction` doesn't return a promise.
 * @param items Array of items to process.
 * @param itemAction Action to be called for every item.
 * @param batchSize Size of batch.
 */
export async function processWithUiBreak<T>(
    items: readonly T[],
    itemAction: (item: T) => Promise<void> | void,
    batchSize = 100
): Promise<void> {
    await processInAsyncBatches(
        items,
        async batch => {
            await runInAction(async () => {
                for (const item of batch) {
                    const possiblePromise = itemAction(item)
                    if (isPromise(possiblePromise)) {
                        await possiblePromise
                    }
                }
            })
        },
        batchSize
    )
}

/**
 * Processes input array of strings into map which has strings as keys and values as count of how many they occured in input array.
 */
export function arrayCounts<T extends string>(array: T[]): DefaultMap<T, number> {
    const counts = new DefaultMap<T, number>(() => 0)
    array.forEach(value => counts.set(value, counts.get(value) + 1))

    return counts
}
